/**
 * Base64 Decode Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const Base64DecodeTool: ITransformerTool = {
  id: 'base64-decode',
  name: 'Base64 Decode',
  description: 'Decode Base64 to text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['base64', 'decode', 'decoding', 'encoding'],

  seo: {
    slug: 'base64-decode',
    pageTitle: 'Base64 Decoder - Free Online Tool',
    metaDescription: 'Decode Base64 encoded strings back to plain text instantly. Supports UTF-8 output. Fast, private, runs entirely in your browser.',
    keywords: ['base64 decode', 'base64 decoder', 'base64 to text', 'decode base64 online'],
    relatedTools: ['base64-encode', 'url-decode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.BASE64],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  supportsInput(input: string): boolean {
    const trimmed = input.replace(/\s+/g, '').trim();
    // Basic Base64 pattern check
    const base64Regex = /^[A-Za-z0-9+/_-]+={0,2}$/;
    return base64Regex.test(trimmed);
  },

  async transform(input: string): Promise<string> {
    try {
      // Handle URL-safe Base64
      let base64 = input.replace(/\s+/g, '').trim();
      base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding if needed
      while (base64.length % 4) {
        base64 += '=';
      }
      
      // Decode
      const decoded = atob(base64);
      
      // Try to decode as UTF-8
      try {
        return decodeURIComponent(escape(decoded));
      } catch {
        // Return as-is if not valid UTF-8
        return decoded;
      }
    } catch (e) {
      throw ToolError.validation('Invalid Base64 string', {
        suggestion: 'Ensure the input contains only valid Base64 characters (A-Z, a-z, 0-9, +, /, =)',
      });
    }
  },
};

